/* ==========================================================================
   CREATE! — Trenner & Sprungnavigation
   ct-sections.js  ·  gemeinsame Logik für:
   ct-divider · ct-section-nav · ct-scroll-dots

   Aufgaben:
   - findet automatisch alle Sprungziele auf der Seite
     (Trenner-Sections, optional zusätzlich Section-Überschriften)
   - berechnet den Offset des sticky Headers
   - smooth scroll + Scrollspy (aktiver Eintrag)
   - Theme-Editor-tauglich (baut sich bei Section-Änderungen neu auf)
   ========================================================================== */

(function () {
  "use strict";

  if (window.CTSections) return;

  var consumers = [];
  var items = [];
  var rafPending = false;
  var lastActive = -1;
  var lockUntil = 0;

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------------------------------------------------------- Utils */

  function slug(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/&/g, " und ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
  }

  function absTop(el) {
    var r = el.getBoundingClientRect();
    return r.top + (window.pageYOffset || document.documentElement.scrollTop);
  }

  var THEME_BARS =
    ".site-header, .header-wrapper, .header-sticky-wrapper, [data-ct-offset]";

  /**
   * Gesamthöhe der oben klebenden Leisten.
   *
   * Sie können gestapelt sein — der Theme-Header an der Fensterkante, die
   * Sprungleiste direkt darunter. Deshalb wird von oben nach unten
   * akkumuliert: eine Leiste zählt, wenn sie an der bisher erreichten
   * Unterkante ansetzt, nicht nur bei Position 0.
   */
  function stackOffset(selector) {
    var bars = [];

    Array.prototype.forEach.call(
      document.querySelectorAll(selector),
      function (el) {
        var cs = window.getComputedStyle(el);
        if (cs.position !== "fixed" && cs.position !== "sticky") return;
        if (cs.display === "none" || cs.visibility === "hidden") return;

        var r = el.getBoundingClientRect();
        if (r.bottom <= 0 || r.height <= 0) return;

        bars.push(r);
      }
    );

    bars.sort(function (a, b) {
      return a.top - b.top;
    });

    var total = 0;
    bars.forEach(function (r) {
      // setzt die Leiste am bisherigen Stapelende an (kleine Toleranz)?
      if (r.top <= total + 2 && r.bottom > total) total = r.bottom;
    });

    return Math.round(total);
  }

  function headerOffset() {
    // beide Varianten: per Script fixiert oder nativ sticky
    return stackOffset(THEME_BARS + ", .ct-nav.is-fixed .ct-nav__bar");
  }

  function setHeaderVar() {
    // Wert OHNE unsere eigene Leiste — sie positioniert sich damit selbst
    // unter dem Theme-Header.
    var total = stackOffset(THEME_BARS);
    document.documentElement.style.setProperty(
      "--ct-header-offset",
      total + "px"
    );
    return total;
  }

  /* ----------------------------------------------------- Sprungziel-Suche */

  function wantsHeadings() {
    // aktiv, sobald mindestens ein Consumer den Überschriften-Fallback will
    for (var i = 0; i < consumers.length; i++) {
      if (consumers[i].opts && consumers[i].opts.headings) return true;
    }
    return false;
  }

  function isSkippable(sec) {
    if (!sec) return true;
    if (sec.hasAttribute("data-ct-nav-skip")) return true;
    if (sec.closest("header, footer")) return true;
    if (/__header|__footer|announcement|ankuendigung/i.test(sec.id)) return true;
    return false;
  }

  function collect() {
    var list = [];
    var useHeadings = wantsHeadings();
    var sections = document.querySelectorAll('[id^="shopify-section-"]');

    // Fallback, falls das Markup nicht in Shopify-Sections liegt (z. B. Vorschau)
    var roots = sections.length
      ? sections
      : [document.body];

    Array.prototype.forEach.call(roots, function (sec) {
      if (sec !== document.body && isSkippable(sec)) return;

      var anchors = sec.querySelectorAll("[data-ct-anchor]");

      if (anchors.length) {
        Array.prototype.forEach.call(anchors, function (a) {
          if (a.getAttribute("data-ct-nav") === "false") return;
          var label = a.getAttribute("data-ct-label") || "";
          if (!label) label = a.getAttribute("data-ct-anchor") || "";
          if (!label) return;
          list.push({
            id: a.id || a.getAttribute("data-ct-anchor"),
            label: label,
            el: a,
            source: "divider"
          });
        });
        return;
      }

      if (!useHeadings || sec === document.body) return;
      if (sec.querySelector("[data-ct-nav-root]")) return; // unsere eigenen Sections
      if (sec.getBoundingClientRect().height < 160) return;

      var h = sec.querySelector("h2, h3, .h2, .section-header__title");
      if (!h) return;

      var text = (h.textContent || "").trim().replace(/\s+/g, " ");
      if (!text || text.length > 44) return;

      if (!h.id) h.id = "ct-" + (slug(text) || "abschnitt");

      list.push({
        id: h.id,
        label: text,
        el: sec,
        source: "heading"
      });
    });

    // doppelte Labels/Ziele entfernen
    var seen = {};
    list = list.filter(function (it) {
      var key = it.id + "|" + it.label;
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });

    return list;
  }

  /* ------------------------------------------------- Automatische Nummern */

  function numberDividers() {
    var n = 0;
    var nodes = document.querySelectorAll("[data-ct-autonum]");
    Array.prototype.forEach.call(nodes, function (el) {
      n++;
      var pad = el.getAttribute("data-ct-autonum") === "pad";
      el.textContent = pad && n < 10 ? "0" + n : String(n);
    });
  }

  /* -------------------------------------------------------------- Scrollen */

  /**
   * Offset, der am Ziel gelten WIRD — nicht der aktuelle.
   * Eine Sprungleiste im Modus „sticky“ klebt sich erst während des
   * Scrollens fest; ohne diese Vorausberechnung würde sie das
   * angesprungene Ziel anschließend verdecken.
   */
  function projectedOffset(targetTop) {
    var base = headerOffset();
    var navs = document.querySelectorAll(
      '.ct-nav[data-ct-mode="sticky"], .ct-nav[data-ct-mode="always"]'
    );

    Array.prototype.forEach.call(navs, function (nav) {
      var sentinel = nav.querySelector(".ct-nav__sentinel");
      var bar = nav.querySelector(".ct-nav__bar");
      if (!sentinel || !bar) return;

      // eine am unteren Rand klebende Leiste verdeckt das Ziel nicht
      if (
        nav.classList.contains("ct-nav--mob-bottom") &&
        window.matchMedia("(max-width: 768px)").matches
      ) {
        return;
      }

      var isFixed = nav.classList.contains("is-fixed");
      var always = nav.getAttribute("data-ct-mode") === "always";
      var willFix = always || absTop(sentinel) < targetTop;

      if (willFix && !isFixed) base += bar.offsetHeight;
      if (!willFix && isFixed) base -= bar.offsetHeight;
    });

    return Math.max(0, base);
  }

  function scrollToItem(item) {
    if (!item || !item.el) return;

    // Sprungleisten vorab in den Zustand versetzen, den sie am Ziel haben
    // werden. Sonst wechseln sie mitten im Scrollen ihren Fixier-Zustand —
    // das Scroll-Anchoring des Browsers verschiebt dann die Zielposition
    // um genau die Leistenhöhe.
    var wanted = absTop(item.el);
    consumers.forEach(function (c) {
      if (c.prepare) c.prepare(wanted);
    });

    var raw = absTop(item.el);
    var target = raw - projectedOffset(raw) - 12;
    if (target < 0) target = 0;

    // Aktiven Zustand sofort setzen und den Scrollspy kurz sperren,
    // damit die Zwischenpositionen der Animation nicht durchflackern.
    var index = items.indexOf(item);
    if (index > -1) {
      lockUntil = Date.now() + (reduceMotion ? 80 : 800);
      lastActive = index;
      consumers.forEach(function (c) {
        if (c.onActive) c.onActive(index, items);
      });
    }

    window.scrollTo({
      top: target,
      behavior: reduceMotion ? "auto" : "smooth"
    });
  }

  function scrollToId(id) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === id) {
        scrollToItem(items[i]);
        return true;
      }
    }
    var el = document.getElementById(id);
    if (el) {
      scrollToItem({ el: el, id: id });
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------- Scrollspy */

  function computeActive() {
    if (!items.length) return -1;

    var scrollY = window.pageYOffset || document.documentElement.scrollTop;
    var line = scrollY + headerOffset() + 28;
    var active = 0;

    for (var i = 0; i < items.length; i++) {
      if (absTop(items[i].el) - 2 <= line) active = i;
    }

    // ganz unten angekommen → letzter Eintrag
    var docH = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    );
    if (scrollY + window.innerHeight >= docH - 4) active = items.length - 1;

    return active;
  }

  function pageProgress() {
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;
    var docH = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    );
    var max = docH - window.innerHeight;
    if (max <= 0) return 0;
    return Math.min(1, Math.max(0, scrollY / max));
  }

  function update(force) {
    rafPending = false;

    var prog = pageProgress();

    // Während der Klick-Animation bleibt der aktive Eintrag stehen;
    // Fortschritt und Fixier-Logik laufen weiter.
    if (!force && Date.now() < lockUntil) {
      consumers.forEach(function (c) {
        if (c.onProgress) c.onProgress(prog, lastActive, items);
      });
      return;
    }

    var active = computeActive();

    if (active !== lastActive || force) {
      lastActive = active;
      consumers.forEach(function (c) {
        if (c.onActive) c.onActive(active, items);
      });
    }

    consumers.forEach(function (c) {
      if (c.onProgress) c.onProgress(prog, active, items);
    });
  }

  function onScroll() {
    if (rafPending) return;
    rafPending = true;
    window.requestAnimationFrame(function () {
      update(false);
    });
  }

  /* ---------------------------------------------------------------- Rebuild */

  var rebuildTimer = null;

  function rebuild() {
    numberDividers();
    setHeaderVar();
    items = collect();
    lastActive = -1;

    consumers.forEach(function (c) {
      if (c.onItems) c.onItems(items);
    });

    update(true);
  }

  function rebuildSoon() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(rebuild, 60);
  }

  /* ------------------------------------------------------------------- API */

  window.CTSections = {
    register: function (consumer) {
      consumers.push(consumer);
      rebuildSoon();
      return {
        destroy: function () {
          var i = consumers.indexOf(consumer);
          if (i > -1) consumers.splice(i, 1);
        }
      };
    },
    rebuild: rebuildSoon,
    getItems: function () {
      return items;
    },
    scrollToItem: scrollToItem,
    scrollToId: scrollToId,
    headerOffset: headerOffset,
    slug: slug,
    reduceMotion: reduceMotion
  };

  /* ---------------------------------------------------------------- Events */

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener(
    "resize",
    function () {
      setHeaderVar();
      update(true);
    },
    { passive: true }
  );

  document.addEventListener("shopify:section:load", rebuildSoon);
  document.addEventListener("shopify:section:unload", rebuildSoon);
  document.addEventListener("shopify:section:reorder", rebuildSoon);

  // Bilder / LazyLoad verschieben Positionen → nachjustieren
  window.addEventListener("load", function () {
    rebuildSoon();
    setTimeout(rebuildSoon, 600);
    setTimeout(rebuildSoon, 1800);
  });

  if ("ResizeObserver" in window) {
    var ro = new ResizeObserver(function () {
      onScroll();
    });
    if (document.body) ro.observe(document.body);
  }

  // Direktaufruf mit #anker — mit Header-Offset nachkorrigieren
  function handleHash() {
    var hash = window.location.hash.replace("#", "");
    if (!hash) return;
    setTimeout(function () {
      window.CTSections.scrollToId(decodeURIComponent(hash));
    }, 320);
  }

  window.addEventListener("hashchange", handleHash);

  function boot() {
    rebuild();
    handleHash();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* ------------------------------------------- Einblenden beim Scrollen */

  if ("IntersectionObserver" in window) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("ct-in");
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.15 }
    );

    var observeReveals = function () {
      var nodes = document.querySelectorAll(".ct-reveal:not(.ct-in), .ct-div--anim:not(.ct-in)");
      Array.prototype.forEach.call(nodes, function (n) {
        revealObserver.observe(n);
      });
    };

    observeReveals();
    document.addEventListener("shopify:section:load", observeReveals);
    window.addEventListener("load", observeReveals);
  } else {
    var fallback = document.querySelectorAll(".ct-reveal, .ct-div--anim");
    Array.prototype.forEach.call(fallback, function (n) {
      n.classList.add("ct-in");
    });
  }
})();
